import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AdminDefinition } from 'app/core/store/admin/admin-definitions/admin-definitions.interface';
import { I18n } from 'app/core/store/admin/i18n/i18n.interface';
import { logger } from 'app/core/util/logger';
import { fadeInAnimation } from '../../../../animations';
import * as fromRoot from '../../../../core/store';
import * as actions from '../../../../core/store/admin/i18n/i18n.actions';
import { LanguageDataChangedAction } from '../../../../core/store/admin/i18n/i18n.actions';
import { Definition } from '../../../../core/store/definitions/definition.interface';
import { util } from '../../../../core/util/util';
import { BaseContainer } from '../../../base/BaseContainer';
import { goAction } from '../../../../core/store/router/router.actions';
import { takeUntil } from 'rxjs/operators';
import { I18Sandbox } from 'app/core/sandboxes/i18n.sandbox';

interface Comparison {
  missingKeys: string[];
  unknownKeys: string[];
}

@Component({
  selector: 'admin-i18n',
  templateUrl: './i18n.container.html',
  styleUrls: ['./i18n.container.scss'],
  animations: [fadeInAnimation],
})
export class I18nContainer extends BaseContainer implements OnInit {
  json: string = null;
  csv: string = null;
  referenceJson: object = null;
  edit: boolean = false;
  languages: Definition[] = [];
  usedLanguage: string;
  errorMessage: string;
  saving: boolean;
  loading: boolean;
  jsonSelected = false;
  dataChanged: boolean;

  constructor(private sandbox: I18Sandbox, private translate: TranslateService) {
    super();
  }

  ngOnInit(): void {
    this.loadReferenceJson();
    this.store
      .select(state => state.admin.adminDefinitions.language)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((languages: AdminDefinition[]) => {
        this.languages = languages;
      });

    this.store
      .select(state => state.i18n.dataChanged)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((dataChanged) => this.dataChanged = dataChanged);

    this.store
      .select(state => state.session.userContext.language)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((language: string) => {
        this.usedLanguage = language;
      });

    this.store
      .select(state => state.i18n)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((i18n: I18n) => {
        this.saving = i18n.saving;
        this.loading = i18n.loading;

        if (this.jsonSelected) {
          this.json = i18n.json || {};
          this.csv = null;
        }
        if (i18n.allCsv) {
          this.csv = i18n.allCsv;
          this.json = null;
        }
      });
    this.onJsonChanged(true);
  }

  private insertMissingKeys(target: any, reference: any): any {
    const comp = this.compareFlattenedJsons(target, reference);
    const result = { ...target };
    comp.missingKeys.forEach(key => (result[key] = 'XxX-TRANSLATION_MISSING-XxX'));
    return result;
  }

  private compareFlattenedJsons(json: any, reference: any): Comparison {
    const refKeys = Object.keys(reference);
    const keys = Object.keys(json);
    const missingKeys = refKeys.filter(key => keys.indexOf(key) === -1);
    const unknownKeys = keys.filter(key => refKeys.indexOf(key) === -1);
    return { missingKeys, unknownKeys };
  }

  validate(jsonString: string): void {
    this.errorMessage = '';
    try {
      const o = JSON.parse(jsonString);
      const json = util.flattenJson(o);
      const ref = util.flattenJson(this.referenceJson);
      const { missingKeys, unknownKeys } = this.compareFlattenedJsons(json, ref);

      if (missingKeys.length) {
        this.errorMessage += `Missing keys: ${missingKeys.concat(', ')}<br>`;
      }
      if (unknownKeys.length) {
        this.errorMessage += `Unknown keys: ${unknownKeys.concat(', ')}<br>`;
      }
    } catch (e) {
      logger.error(e);
      this.errorMessage = 'Invalid json: ' + e;
    }
  }

  save(jsonOrCsv: string): void {
    if (this.csv) {
      this.store.dispatch(new actions.LanguageSaveAllAction(jsonOrCsv));
    } else {
      if (typeof jsonOrCsv === 'object') {
        // json
        this.errorMessage = '';
      } else {
        // excel format
        this.validate(jsonOrCsv);
      }
      if (this.errorMessage === '') {
        let jsonToSave;
        if (typeof jsonOrCsv === 'object') {
          jsonToSave = util.unflattenJson(jsonOrCsv);
        } else {
          jsonToSave = util.unflattenJson(JSON.parse(jsonOrCsv));
        }
        this.store.dispatch(new actions.LanguageSaveAction({ language: this.usedLanguage, json: jsonToSave }));
      }
    }
  }

  load(): void {
  }

  editLanguage(language: string) {
    this.store.dispatch(new actions.LanguageLoadAction({ language }));
  }

  editAllCsv() {
    this.store.dispatch(new LanguageDataChangedAction(false));
    this.store.dispatch(new actions.LanguageLoadAllAction());
  }

  testLanguage(language: string) {
    this.translate.use(language);
  }

  getTranslations(): string {
    const loadedTranslations = this.translate.store.translations;
    const flattened = util.flattenJson(loadedTranslations);
    return JSON.stringify(flattened, null, 2);
  }

  resetLanguages() {
    this.store.dispatch(goAction({ path: '/admin/i18n/reset' }));
  }

  onJsonChanged(jsonSelected: boolean) {
    this.jsonSelected = jsonSelected;
    if (jsonSelected && this.json === null) {
      !this.saving && this.editLanguage(this.usedLanguage);
    }
  }

  contentChanged() {
    if (!this.dataChanged) {
      this.store.dispatch(new LanguageDataChangedAction(true));
    }
  }

  onChangeSelectedJsonLanguage(selectedJsonLanguage: string) {
    this.store.dispatch(new LanguageDataChangedAction(false));
    this.usedLanguage = selectedJsonLanguage;
  }

  private loadReferenceJson() {
    this.sandbox
      .referenceJson$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(json => {
        this.referenceJson = json;
      });
  }
}
